/**
 * Sign in. Presented as the gold band the rest of the console never uses — the one place
 * the accent takes the whole surface, and the reason --gold-surface exists as a token
 * separate from --gold.
 */
import { useState, type ReactElement } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, post } from "../lib/api";
import type { SessionResponse } from "../lib/types";
import { AuthFrame } from "../components/AuthFrame";

export function Login(): ReactElement {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = params.get("next") ?? "/documents";

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await post<SessionResponse>("/v1/sessions", { email, password });
      // The gate's ["me"] query was answered before this login existed.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      navigate(next, { replace: true });
    } catch (cause) {
      // 429 reads differently from 401 on purpose: "too many attempts" is actionable in
      // a way "wrong password" is not. Neither says which half was wrong, because the
      // server does not either.
      setError(
        cause instanceof ApiError
          ? cause.message
          : cause instanceof Error
            ? cause.message
            : "Could not reach the server.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame>
      <form onSubmit={submit} className="border border-hair bg-surface p-6">
        <label htmlFor="email" className="eyebrow">
          // email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          className="mt-3 w-full border border-hair bg-ground px-3 py-2.5 font-mono text-[13px] text-text outline-none transition-colors duration-200 ease-alt placeholder:text-faint focus:border-gold"
        />

        <label htmlFor="password" className="eyebrow mt-5 block">
          // password
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="mt-3 w-full border border-hair bg-ground px-3 py-2.5 font-mono text-[13px] text-text outline-none transition-colors duration-200 ease-alt placeholder:text-faint focus:border-gold"
        />

        {error && (
          <p className="mt-4 border border-crit/40 bg-crit-wash px-3 py-2 text-[12.5px] leading-relaxed text-crit">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 flex w-full items-center justify-center gap-2 border border-gold-surface bg-gold-surface px-3 py-2.5 font-mono text-[11px] font-bold tracking-[0.2em] text-on-gold uppercase transition-colors duration-200 ease-alt hover:bg-gold-soft disabled:opacity-50"
        >
          {busy ? "signing in…" : "sign in"}
        </button>
      </form>

      <p className="mt-5 font-mono text-[10.5px] tracking-[0.12em] text-faint">
        &gt; no account? ask an owner for an invitation
      </p>
    </AuthFrame>
  );
}
